import {
  AddOutlined,
  AllInboxOutlined,
  ArchiveOutlined,
  AutorenewOutlined,
  EditOutlined,
  SearchOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PackagingDefinition,
  archivePackagingDefinition,
  createPackagingDefinition,
  isAuthenticationError,
  loadApplicationSettings,
  loadPackagingDefinitions,
  updatePackagingDefinition
} from "./api";
import { login } from "./auth";
import { PageHeader } from "./PageHeader";
import { ListRowActions } from "./ListRowActions";
import {
  clampNonNegativeMeasurementInput, formatMeasurement, fromMetric, measurementConfiguration,
  measurementInputStep, measurementUnit, toMetric
} from "./measurements";
import {
  compareOptionalDates,
  matchesListSyntax,
  normalizeSearch
} from "./listSearch";

const organizationId = "10000000-0000-0000-0000-000000000001";
const packagingTypes: PackagingDefinition["packagingType"][] = [
  "EACH", "INNER_PACK", "BOX", "TOTE", "CRATE", "PALLET", "CONTAINER", "OTHER"
];

export function PackagingMasterData({
  onBack,
  permissions
}: {
  onBack: () => void;
  permissions: string[];
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [type, setType] = useState<PackagingDefinition["packagingType"]>("BOX");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [tareWeight, setTareWeight] = useState("0");
  const [maxGrossWeight, setMaxGrossWeight] = useState("");
  const [rotationAllowed, setRotationAllowed] = useState(true);
  const [stackable, setStackable] = useState(true);
  const [maxStackCount, setMaxStackCount] = useState("");
  const [reusable, setReusable] = useState(false);
  const [listTab, setListTab] = useState<"active" | "closed">("active");
  const [identifierFilter, setIdentifierFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [reusableFilter, setReusableFilter] = useState("");
  const [sortDate, setSortDate] =
    useState<"createdAt" | "updatedAt">("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [syntaxSearch, setSyntaxSearch] = useState("");
  const [editedDefinition, setEditedDefinition] =
    useState<PackagingDefinition | null>(null);
  const [definitionToArchive, setDefinitionToArchive] =
    useState<PackagingDefinition | null>(null);
  const canManage = permissions.includes("packaging:manage");

  const definitions = useQuery({
    queryKey: ["packaging-definitions"],
    queryFn: loadPackagingDefinitions
  });
  const applicationSettings = useQuery({
    queryKey: ["application-settings"], queryFn: loadApplicationSettings
  });
  const measurements = useMemo(() => measurementConfiguration(
    applicationSettings.data
  ), [applicationSettings.data]);
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "fr-FR";
  const lengthUnit = measurementUnit("LENGTH", measurements);
  const massUnit = measurementUnit("MASS", measurements);
  const lengthStep = measurementInputStep("LENGTH", measurements);
  const massStep = measurementInputStep("MASS", measurements);
  const create = useMutation({
    mutationFn: () => createPackagingDefinition({
      organizationId,
      code,
      displayName,
      packagingType: type,
      innerLengthMm: toMetric(Number(length), "LENGTH", measurements),
      innerWidthMm: toMetric(Number(width), "LENGTH", measurements),
      innerHeightMm: toMetric(Number(height), "LENGTH", measurements),
      tareWeightKg: toMetric(Number(tareWeight), "MASS", measurements),
      maxGrossWeightKg: maxGrossWeight
        ? toMetric(Number(maxGrossWeight), "MASS", measurements) : null,
      rotationAllowed,
      stackable,
      maxStackCount: stackable && maxStackCount ? Number(maxStackCount) : null,
      reusable
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["packaging-definitions"] });
      setDialogOpen(false);
      setCode("");
      setDisplayName("");
      setLength("");
      setWidth("");
      setHeight("");
      setMaxGrossWeight("");
      setMaxStackCount("");
    }
  });
  const update = useMutation({
    mutationFn: () => updatePackagingDefinition(editedDefinition!.id, {
      displayName,
      packagingType: type,
      innerLengthMm: toMetric(Number(length), "LENGTH", measurements),
      innerWidthMm: toMetric(Number(width), "LENGTH", measurements),
      innerHeightMm: toMetric(Number(height), "LENGTH", measurements),
      tareWeightKg: toMetric(Number(tareWeight), "MASS", measurements),
      maxGrossWeightKg: maxGrossWeight
        ? toMetric(Number(maxGrossWeight), "MASS", measurements) : null,
      rotationAllowed,
      stackable,
      maxStackCount: stackable && maxStackCount
        ? Number(maxStackCount) : null,
      reusable,
      version: editedDefinition!.version
    }),
    onSuccess: async () => {
      setDialogOpen(false);
      setEditedDefinition(null);
      await queryClient.invalidateQueries({
        queryKey: ["packaging-definitions"]
      });
    }
  });
  const archive = useMutation({
    mutationFn: () => archivePackagingDefinition(
      definitionToArchive!.id, definitionToArchive!.version
    ),
    onSuccess: async () => {
      setDefinitionToArchive(null);
      await queryClient.invalidateQueries({
        queryKey: ["packaging-definitions"]
      });
    }
  });
  const filteredDefinitions = useMemo(() =>
    [...(definitions.data ?? [])].filter((definition) =>
      (definition.status === "ACTIVE") === (listTab === "active")
      && (!identifierFilter
        || normalizeSearch(`${definition.id} ${definition.code}`)
          .includes(normalizeSearch(identifierFilter)))
      && (!typeFilter || definition.packagingType === typeFilter)
      && (!reusableFilter
        || String(definition.reusable) === reusableFilter)
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${definition.id} ${definition.code}`,
        libelle: definition.displayName,
        type: definition.packagingType,
        statut: definition.status,
        reutilisable: definition.reusable,
        dimension: `${definition.innerLengthMm}x${definition.innerWidthMm}x${definition.innerHeightMm}`,
        date: `${definition.createdAt} ${definition.updatedAt}`
      }, syntaxSearch))
    ).sort((left, right) => compareOptionalDates(
      left[sortDate], right[sortDate], sortDirection
    )), [
      definitions.data, identifierFilter, listTab, reusableFilter,
      sortDate, sortDirection, syntaxSearch, typeFilter
    ]);
  const activeCount = definitions.data?.filter(
    (definition) => definition.status === "ACTIVE"
  ).length ?? 0;
  const closedCount = (definitions.data?.length ?? 0) - activeCount;

  function submit(event: FormEvent) {
    event.preventDefault();
    if ([length, width, height, tareWeight].some(
      (value) => value === "" || Number(value) < 0
    ) || (maxGrossWeight !== "" && Number(maxGrossWeight) < 0)) return;
    if (editedDefinition) update.mutate();
    else create.mutate();
  }

  function edit(definition: PackagingDefinition) {
    setEditedDefinition(definition);
    setCode(definition.code);
    setDisplayName(definition.displayName);
    setType(definition.packagingType);
    setLength(String(fromMetric(definition.innerLengthMm, "LENGTH", measurements)));
    setWidth(String(fromMetric(definition.innerWidthMm, "LENGTH", measurements)));
    setHeight(String(fromMetric(definition.innerHeightMm, "LENGTH", measurements)));
    setTareWeight(String(fromMetric(definition.tareWeightKg, "MASS", measurements)));
    setMaxGrossWeight(definition.maxGrossWeightKg === null
      ? "" : String(fromMetric(definition.maxGrossWeightKg, "MASS", measurements)));
    setRotationAllowed(definition.rotationAllowed);
    setStackable(definition.stackable);
    setMaxStackCount(definition.maxStackCount === null
      ? "" : String(definition.maxStackCount));
    setReusable(definition.reusable);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditedDefinition(null);
  }

  return (
    <Stack spacing={1.5} sx={{
      height: { md: "100%" }, minHeight: 0
    }}>
      <PageHeader
        title={t("masterData.packaging.title")}
        compact
        onBack={onBack}
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddOutlined />}
            onClick={() => {
              setEditedDefinition(null);
              setCode("");
              setDisplayName("");
              setLength("");
              setWidth("");
              setHeight("");
              setDialogOpen(true);
            }}>
            {t("masterData.packaging.new")}
          </Button>
        ) : undefined}
      />

      {definitions.isError && (
        <Alert
          severity="error"
          action={isAuthenticationError(definitions.error)
            ? <Button color="inherit" size="small" onClick={() => void login()}>
                {t("masterData.common.reconnect")}
              </Button>
            : <Button color="inherit" size="small"
                onClick={() => void definitions.refetch()}>
                {t("masterData.common.retry")}
              </Button>}
        >
          {t("masterData.packaging.loadError")}
        </Alert>
      )}
      {!definitions.isLoading && !definitions.isError &&
        definitions.data?.length === 0 && (
          <Alert severity="info">
            {t("masterData.packaging.empty")}
          </Alert>
        )}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack spacing={1.5}>
            <TextField label={t("masterData.common.search")}
              placeholder={'id:BOX type:PALLET reutilisable:true'}
              value={syntaxSearch}
              onChange={(event) => setSyntaxSearch(event.target.value)}
              InputProps={{ startAdornment:
                <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField label={t("masterData.packaging.identifier")} value={identifierFilter}
                onChange={(event) => setIdentifierFilter(event.target.value)}
                sx={{ flex: 1 }} />
              <FormControl sx={{ flex: 1, minWidth: 170 }}>
                <InputLabel>{t("masterData.common.type")}</InputLabel>
                <Select label={t("masterData.common.type")} value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}>
                  <MenuItem value="">{t("masterData.common.all")}</MenuItem>
                  {packagingTypes.map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 170 }}>
                <InputLabel>{t("masterData.packaging.reusable")}</InputLabel>
                <Select label={t("masterData.packaging.reusable")} value={reusableFilter}
                  onChange={(event) => setReusableFilter(event.target.value)}>
                  <MenuItem value="">{t("masterData.common.all")}</MenuItem>
                  <MenuItem value="true">{t("masterData.common.yes")}</MenuItem>
                  <MenuItem value="false">{t("masterData.common.no")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 170 }}>
                <InputLabel>{t("masterData.packaging.sortDate")}</InputLabel>
                <Select label={t("masterData.packaging.sortDate")} value={sortDate}
                  onChange={(event) => setSortDate(event.target.value as
                    "createdAt" | "updatedAt")}>
                  <MenuItem value="createdAt">{t("masterData.common.created")}</MenuItem>
                  <MenuItem value="updatedAt">{t("masterData.common.updated")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>{t("masterData.common.order")}</InputLabel>
                <Select label={t("masterData.common.order")} value={sortDirection}
                  onChange={(event) => setSortDirection(event.target.value as
                    "asc" | "desc")}>
                  <MenuItem value="asc">{t("masterData.common.ascending")}</MenuItem>
                  <MenuItem value="desc">{t("masterData.common.descending")}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Tabs value={listTab}
        onChange={(_, value: "active" | "closed") => setListTab(value)}
      >
        <Tab value="active" label={t("masterData.common.activeCount", { count: activeCount })} />
        <Tab value="closed" label={t("masterData.common.archivedCount", { count: closedCount })} />
      </Tabs>
      <Box sx={{
        minHeight: 0, flex: { md: 1 }, overflowY: { md: "auto" },
        pr: { md: .5 }
      }}>
      {definitions.data?.length !== 0 && filteredDefinitions.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("masterData.packaging.noFilterResult")}
        </Alert>
      )}
      <Stack spacing={1.5}>
        {filteredDefinitions.map((definition) => (
          <Card key={definition.id} variant="outlined">
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
                alignItems={{ sm: "center" }}>
                <AllInboxOutlined color="secondary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={800}>
                    {definition.code} · {definition.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMeasurement(definition.innerLengthMm, "LENGTH", measurements, locale)} × {formatMeasurement(definition.innerWidthMm, "LENGTH", measurements, locale)} ×
                    {" "}{formatMeasurement(definition.innerHeightMm, "LENGTH", measurements, locale)} · tare {formatMeasurement(definition.tareWeightKg, "MASS", measurements, locale)}
                    {definition.maxGrossWeightKg
                      ? ` · charge brute max. ${formatMeasurement(definition.maxGrossWeightKg, "MASS", measurements, locale)}`
                      : ""}
                  </Typography>
                </Box>
                <ListRowActions actions={
                  <Stack direction="row" spacing={1}>
                    <Chip label={definition.packagingType} />
                  {definition.reusable && (
                    <Chip icon={<AutorenewOutlined />} label={t("masterData.packaging.reusable")} />
                  )}
                  {canManage && definition.status === "ACTIVE" && (
                    <>
                      <Button variant="outlined" startIcon={<EditOutlined />}
                        onClick={() => edit(definition)}>
                        {t("masterData.common.edit")}
                      </Button>
                      <Button variant="outlined" color="error"
                        startIcon={<ArchiveOutlined />}
                        onClick={() => setDefinitionToArchive(definition)}>
                        {t("masterData.common.archive")}
                      </Button>
                    </>
                  )}
                  </Stack>
                } status={
                  <Chip color={definition.status === "ACTIVE"
                    ? "success" : "default"} label={t(`masterData.common.statuses.${definition.status}`, { defaultValue: definition.status })} />
                } />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog}
        fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>
            {editedDefinition
              ? t("masterData.packaging.editMaster", { code: editedDefinition.code })
              : t("masterData.packaging.createMaster")}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField label={t("masterData.common.code")} required value={code}
                disabled={editedDefinition !== null}
                placeholder="Ex. BOX-400-300-250"
                onChange={(event) => setCode(event.target.value.toUpperCase())} />
              <TextField label={t("masterData.common.name")} required value={displayName}
                onChange={(event) => setDisplayName(event.target.value)} />
              <FormControl fullWidth required>
                <InputLabel>{t("masterData.common.type")}</InputLabel>
                <Select value={type} label={t("masterData.common.type")}
                  onChange={(event) =>
                    setType(event.target.value as PackagingDefinition["packagingType"])}>
                  {packagingTypes.map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="subtitle2">{t("masterData.packaging.interiorDimensions")}</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField type="number" label={`${t("masterData.packaging.length")} (${lengthUnit})`} required value={length}
                  inputProps={{ min: 0, step: lengthStep }}
                  onChange={(event) => setLength(clampNonNegativeMeasurementInput(event.target.value))} />
                <TextField type="number" label={`${t("masterData.packaging.width")} (${lengthUnit})`} required value={width}
                  inputProps={{ min: 0, step: lengthStep }}
                  onChange={(event) => setWidth(clampNonNegativeMeasurementInput(event.target.value))} />
                <TextField type="number" label={`${t("masterData.packaging.height")} (${lengthUnit})`} required value={height}
                  inputProps={{ min: 0, step: lengthStep }}
                  onChange={(event) => setHeight(clampNonNegativeMeasurementInput(event.target.value))} />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField type="number" label={`${t("masterData.packaging.tareWeight")} (${massUnit})`} required
                  value={tareWeight} inputProps={{ min: 0, step: massStep }}
                  onChange={(event) => setTareWeight(clampNonNegativeMeasurementInput(event.target.value))} />
                <TextField type="number" label={`${t("masterData.packaging.maxGrossWeight")} (${massUnit})`}
                  value={maxGrossWeight} inputProps={{ min: massStep, step: massStep }}
                  onChange={(event) => setMaxGrossWeight(clampNonNegativeMeasurementInput(event.target.value))} />
                {stackable && (
                  <TextField type="number" label={t("masterData.packaging.maxStack")}
                    value={maxStackCount} inputProps={{ min: 1, step: 1 }}
                    onChange={(event) => setMaxStackCount(event.target.value)} />
                )}
              </Stack>
              <FormControlLabel control={<Checkbox checked={rotationAllowed}
                onChange={(event) => setRotationAllowed(event.target.checked)} />}
                label={t("masterData.packaging.rotationAllowed")} />
              <FormControlLabel control={<Checkbox checked={stackable}
                onChange={(event) => setStackable(event.target.checked)} />}
                label={t("masterData.packaging.stackable")} />
              <FormControlLabel control={<Checkbox checked={reusable}
                onChange={(event) => setReusable(event.target.checked)} />}
                label={t("masterData.packaging.reusablePackaging")} />
              {(create.isError || update.isError) && (
                <Alert severity="error">
                  {t("masterData.packaging.invalidValues")}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>{t("masterData.common.cancel")}</Button>
            <Button type="submit" variant="contained"
              disabled={create.isPending || update.isPending}>
              {create.isPending || update.isPending
                ? t("masterData.packaging.saving") : editedDefinition
                  ? t("masterData.common.save") : t("masterData.common.create")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={definitionToArchive !== null}
        onClose={() => setDefinitionToArchive(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("masterData.packaging.archiveTitle", { code: definitionToArchive?.code })}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t("masterData.packaging.archiveHelp")}
          </Alert>
          {archive.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t("masterData.packaging.archiveError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDefinitionToArchive(null)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained" color="error"
            disabled={archive.isPending} onClick={() => archive.mutate()}>
            {t("masterData.common.archive")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
